import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { PageDto, PageQueriesModel } from './../shared';
import { ProductImageDto } from './image.dto';
import { ProductDto } from './product.dto';

type ProductConfigurationQueries = {
  product: ProductDto;
  clientId: string;
  productLineId: string;
  baseProductId: string;
};

@Injectable()
export class ProductRestService {
  private productManagementUrl = 'api/productManagement';

  imageChange: BehaviorSubject<ProductImageDto[]> = new BehaviorSubject<
    ProductImageDto[]
  >([]);
  get imageData(): ProductImageDto[] {
    return this.imageChange.value;
  }

  constructor(private httpClient: HttpClient) {}

  getList(
    queries: PageQueriesModel
  ): Observable<PageDto<ProductDto>> {
    const url = `${this.productManagementUrl}/products`;
    const options = {
      params: queries.getParams()
    };

    return this.httpClient.get(url, options).pipe(
      map((response: PageDto<ProductDto>) => new PageDto(response)),
      catchError(this.handleError)
    );
  }

  getListByClientId(
    queries: PageQueriesModel,
    clientId: string
  ): Observable<PageDto<ProductDto>> {
    const url = `${this.productManagementUrl}/clients/${clientId}/products`;
    const options = {
      params: queries.getParams()
    };

    return this.httpClient.get(url, options).pipe(
      map((response: PageDto<ProductDto>) => new PageDto(response)),
      catchError(this.handleError)
    );
  }

  getListByProductLineId(
    queries: PageQueriesModel,
    clientId: string,
    productLineId: string
  ) {
    const url = `${
      this.productManagementUrl
    }/clients/${clientId}/productlines/${productLineId}/products`;
    const options = {
      params: queries.getParams()
    };

    return this.httpClient.get(url, options).pipe(
      map((res: PageDto<ProductDto>) => new PageDto(res)),
      catchError(this.handleError)
    );
  }

  get(id: string): Observable<ProductDto> {
    const url = `${this.productManagementUrl}/products/${id}`;

    return this.httpClient.get(url).pipe(
      map((response: ProductDto) => new ProductDto(response)),
      catchError(this.handleError)
    );
  }

  post(
    product: ProductDto,
    clientId: string,
    productLineId: string
  ): Observable<ProductDto> {
    const url = `${
      this.productManagementUrl
    }/clients/${clientId}/productlines/${productLineId}/products`;

    return this.httpClient.post(url, product).pipe(
      map((res: ProductDto) => new ProductDto(res)),
      catchError(this.handleError)
    );
  }

  createConfiguration({
    product,
    clientId,
    productLineId,
    baseProductId
  }: ProductConfigurationQueries): Observable<ProductDto> {
    const url = `${
      this.productManagementUrl
    }/clients/${clientId}/productlines/${productLineId}/products/${baseProductId}`;

    return this.httpClient.post(url, product).pipe(
      map((res: ProductDto) => new ProductDto(res)),
      catchError(this.handleError)
    );
  }

  put(
    productId: string,
    product: ProductDto,
    clientId: string,
    productLineId: string,
    autoupdate?: boolean
  ): Observable<ProductDto> {
    const url = `${
      this.productManagementUrl
    }/clients/${clientId}/productlines/${productLineId}/products/${productId}`;
    const headers = new HttpHeaders({
      ['x-events-variants-autoupdate']: `${autoupdate}`
    });

    return this.httpClient.put(url, product, { headers: headers }).pipe(
      map((res: ProductDto) => new ProductDto(res)),
      catchError(this.handleError)
    );
  }

  patch(
    productId: string,
    product: ProductDto,
    clientId: string,
    productLineId: string
  ): Observable<ProductDto> {
    const url = `${
      this.productManagementUrl
    }/clients/${clientId}/productlines/${productLineId}/products/${productId}`;

    return this.httpClient.patch(url, product).pipe(
      map((res: ProductDto) => new ProductDto(res)),
      catchError(this.handleError)
    );
  }

  delete(productId: string, clientId: string, productLineId: string) {
    const url = `${
      this.productManagementUrl
    }/clients/${clientId}/productlines/${productLineId}/products/${productId}`;

    return this.httpClient.delete(url);
  }

  getEDesignStatusById(productLineId: string, edesignId: string) {
    const url = `${
      this.productManagementUrl
    }/edesigns/${edesignId}/productlines/${productLineId}/products`;

    return this.httpClient.get(url).pipe(
      map((res: { status: string }) => res.status),
      catchError(this.handleError)
    );
  }

  private handleError(errorResponse: any): Promise<any> {
    console.error('An error in ProductRestService: ', errorResponse);
    return Promise.reject(errorResponse.error.errors);
  }
}
